import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper, Chip, CircularProgress, useTheme } from '@mui/material';
import { InfoOutlined, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { format, isToday, isYesterday } from 'date-fns';
import { sk } from 'date-fns/locale';
import gitService from '../services/git.service';
import { usePreferences } from '../contexts/PreferencesContext';
import { getThemeColors } from '../config/theme';
import GitCommitDetailDrawer from './GitCommitDetailDrawer';

const GitActivity = () => {
    const theme = useTheme();
    const { isDarkMode } = usePreferences();
    const colors = getThemeColors(isDarkMode);
    const [gitCommits, setGitCommits] = useState([]);
    const [gitLoading, setGitLoading] = useState(true);
    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const observerTarget = useRef(null);
    const [selectedCommit, setSelectedCommit] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const loadGitActivity = useCallback(async (after = null, append = false) => {
        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setGitLoading(true);
            }

            const response = await gitService.getGitActivity(20, after);
            const responseData = response.data;
            
            let commitsData = [];
            let nextCursorValue = null;

            if (Array.isArray(responseData)) {
                commitsData = responseData;
                nextCursorValue = null;
            } else if (responseData && responseData.data) {
                commitsData = responseData.data;
                nextCursorValue = responseData.paging?.next_cursor || null;
            }

            if (append) {
                setGitCommits(prev => [...prev, ...commitsData]);
            } else {
                setGitCommits(commitsData);
            }

            setNextCursor(nextCursorValue);
            setHasMore(nextCursorValue !== null);
        } catch (error) {
            console.error("Failed to fetch git activity", error);
        } finally {
            setGitLoading(false);
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        loadGitActivity();
    }, [loadGitActivity]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !gitLoading) {
                    loadGitActivity(nextCursor, true);
                }
            },
            { threshold: 0.1 }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [hasMore, nextCursor, loadingMore, gitLoading, loadGitActivity]);

    const handleCommitClick = (commit) => {
        setSelectedCommit(commit);
        setIsDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
        setSelectedCommit(null);
    };

    return (
        <Paper 
            sx={{ 
                borderRadius: 4, 
                bgcolor: colors.backgroundSecondary,
                color: colors.textPrimary,
                boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.2)',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: '600px',
                overflow: 'hidden'
            }}
        >
            <Box sx={{ p: 3, borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: colors.textPrimary }}>
                        Posledná aktivita
                    </Typography>
                </Box>
                <Chip 
                    label="ONLINE" 
                    size="small" 
                    sx={{ 
                        bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)', 
                        color: isDarkMode ? '#93c5fd' : colors.info,
                        fontSize: '0.7rem',
                        height: 20
                    }} 
                />
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {gitLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress sx={{ color: colors.info }} />
                    </Box>
                ) : gitCommits.length > 0 ? (
                    <Box>
                        {gitCommits.map((commit) => {
                            const commitDate = new Date(commit.created_at);
                            const formatDate = () => {
                                if (isToday(commitDate)) {
                                    return `Dnes ${format(commitDate, 'HH:mm')}`;
                                }
                                if (isYesterday(commitDate)) {
                                    return `Včera ${format(commitDate, 'HH:mm')}`;
                                }
                                return format(commitDate, 'd.M.yyyy HH:mm', { locale: sk });
                            };
                            
                            return (
                                <Box 
                                    key={commit.id} 
                                    onClick={() => handleCommitClick(commit)}
                                    sx={{ 
                                        display: 'flex', 
                                        gap: 1.5, 
                                        mb: 2,
                                        position: 'relative',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            opacity: 0.8,
                                        },
                                        '&:not(:last-child)::after': {
                                            content: '""',
                                            position: 'absolute',
                                            left: '7px',
                                            top: '20px',
                                            bottom: '-16px',
                                            width: '2px',
                                            bgcolor: colors.border,
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
                                        <Box 
                                            sx={{ 
                                                width: 8, 
                                                height: 8, 
                                                borderRadius: '50%', 
                                                bgcolor: colors.info,
                                                boxShadow: isDarkMode ? '0 0 8px rgba(59, 130, 246, 0.6)' : '0 0 8px rgba(59, 130, 246, 0.4)'
                                            }} 
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, pb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5, flexWrap: 'wrap' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: colors.info }}>
                                                {commit.author}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: colors.textTertiary, fontSize: '0.7rem', ml: 'auto' }}>
                                                {formatDate()}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 0.5, lineHeight: 1.6 }}>
                                            {commit.message}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={commit.tag}
                                                size="small"
                                                sx={{
                                                    height: 18,
                                                    fontSize: '0.65rem',
                                                    bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.8)' : colors.backgroundTertiary,
                                                    color: colors.textTertiary,
                                                    border: `1px solid ${colors.border}`,
                                                }}
                                            />
                                            {commit.hash && (
                                                <Typography variant="caption" sx={{ color: colors.textTertiary, fontFamily: 'monospace', fontSize: '0.65rem' }}>
                                                    {commit.hash}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                        {hasMore && (
                            <Box ref={observerTarget} sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                {loadingMore && <CircularProgress size={24} sx={{ color: colors.info }} />}
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ p: 4, textAlign: 'center', color: colors.textTertiary }}>
                        <InfoOutlined sx={{ fontSize: 48, mb: 2, color: colors.textTertiary }} />
                        <Typography variant="body1" sx={{ color: colors.textTertiary }}>
                            No Git activity found.
                        </Typography>
                    </Box>
                )}
            </Box>
            <GitCommitDetailDrawer
                commit={selectedCommit}
                open={isDrawerOpen}
                onClose={handleCloseDrawer}
            />
        </Paper>
    );
};

export default GitActivity;




